import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  Calendar,
  Star,
  Newspaper,
  CheckCircle2,
} from "lucide-react";
import { EditorState, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";

import { colors, customShadows } from "../../../../theme";
import { CommonDialog, CommonButton, CommonChip } from "../../ui";

/**
 * NewsPreviewModal Component
 * Shows a full realistic preview of an article before or after publishing.
 */
const NewsPreviewModal = ({ open, onClose, article }) => {
  if (!article) return null;

  const {
    title,
    description,
    imageUrl,
    featured = false,
    updatedOn,
    createdOn,
  } = article;

  const displayDate = updatedOn || createdOn;

  const formatDate = (dateVal) => {
    if (!dateVal) return "Just now";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "Just now";
      return d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Just now";
    }
  };

  // Convert HTML description into EditorState for clean readonly rendering
  let editorState = EditorState.createEmpty();
  if (description) {
    try {
      const contentBlock = htmlToDraft(description);
      if (contentBlock && contentBlock.contentBlocks) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
          contentBlock.entityMap
        );
        editorState = EditorState.createWithContent(contentState);
      }
    } catch (e) {
      console.error("Draft preview parsing error:", e);
    }
  }

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      disableScrollLock={false}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: colors.primary.lighter,
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Newspaper size={20} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 700, color: colors.slate[800], fontSize: "1.1rem" }}
            >
              Article Preview
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[400] }}>
              Live public view preview
            </Typography>
          </Box>
        </Box>
      }
      showCloseButton
      maxWidth="md"
      fullWidth
      actions={
        <CommonButton variant="primary" onClick={onClose}>
          Close Preview
        </CommonButton>
      }
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Badges & Meta */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {featured ? (
            <CommonChip
              label="Featured Article"
              startIcon={<Star size={12} fill="#eab308" color="#eab308" />}
              size="small"
              sx={{
                bgcolor: "#fef9c3",
                color: "#854d0e",
                fontWeight: 700,
                border: "1px solid #fde047",
              }}
            />
          ) : (
            <CommonChip
              label="Official News"
              startIcon={<CheckCircle2 size={12} color={colors.primary.main} />}
              size="small"
              sx={{
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
                fontWeight: 600,
              }}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Calendar size={14} color={colors.slate[400]} />
            <Typography
              variant="caption"
              sx={{ color: colors.slate[500], fontWeight: 500 }}
            >
              {formatDate(displayDate)}
            </Typography>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            lineHeight: 1.25,
            mb: 2.5,
            fontSize: { xs: "1.35rem", sm: "1.75rem" },
          }}
        >
          {title || "Untitled Article"}
        </Typography>

        {/* Cover Image */}
        {imageUrl && (
          <Box
            sx={{
              width: "100%",
              maxHeight: 380,
              borderRadius: "16px",
              overflow: "hidden",
              mb: 3,
              bgcolor: colors.slate[100],
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: customShadows.cardFlat,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              sx={{
                width: "100%",
                height: "100%",
                maxHeight: 380,
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 3, borderColor: colors.dashboard.tableBorder }} />

        {/* Article Body Content */}
        <Box
          sx={{
            fontSize: "1rem",
            color: colors.slate[700],
            lineHeight: 1.7,
            "& .rdw-editor-main": {
              p: 0,
            },
            "& p": {
              mb: 1.5,
            },
            "& h1, & h2, & h3": {
              fontWeight: 700,
              color: colors.slate[900],
              mt: 2,
              mb: 1,
            },
            "& a": {
              color: colors.primary.main,
              textDecoration: "underline",
            },
            "& blockquote": {
              borderLeft: `4px solid ${colors.primary.main}`,
              pl: 2,
              py: 0.5,
              my: 2,
              fontStyle: "italic",
              bgcolor: colors.primary.lighter,
              borderRadius: "0 8px 8px 0",
            },
          }}
        >
          <Editor editorState={editorState} toolbarHidden readOnly />
        </Box>
      </Box>
    </CommonDialog>
  );
};

NewsPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  article: PropTypes.object,
};

export default NewsPreviewModal;
